import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search as SearchIcon, SearchX } from "lucide-react";
import { EmptyState, PageHeader, Spinner, TitleGrid } from "@/components/catalog/TitleGrid";
import { useProfiles } from "@/context/ProfileContext";
import { api } from "@/lib/api";

const SUGGESTIONS = ["Action", "Romance", "Sci-Fi", "Thriller", "Dario Kane", "Aurelio Vance", "Monaco", "Tokyo"];

export default function Search() {
  const { activeProfile } = useProfiles();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [text, setText] = useState(q);
  const pushed = useRef(q);
  const [results, setResults] = useState(null);
  const [all, setAll] = useState([]);

  // Adopt external URL changes (header pill, back nav) without clobbering local typing.
  useEffect(() => {
    if (q !== pushed.current) {
      pushed.current = q;
      setText(q);
    }
  }, [q]);

  const search = (value) => {
    setText(value);
    pushed.current = value;
    setParams(value ? { q: value } : {}, { replace: true });
  };

  useEffect(() => { api.get("/catalog/titles").then((r) => setAll(r.data)).catch(() => {}); }, [activeProfile?.id]);
  useEffect(() => {
    const term = text.trim();
    if (!term) return setResults(null);
    setResults(undefined);
    let cancelled = false;
    const t = setTimeout(() => api.get("/catalog/search", { params: { q: term } }).then((r) => !cancelled && setResults(r.data)).catch(() => !cancelled && setResults([])), 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [text, activeProfile?.id]);

  return (
    <div data-testid="search-page">
      <PageHeader eyebrow="Discover" title="Search">
        <label className="mt-8 flex max-w-3xl items-center gap-3 rounded-full border border-[#D4AF37]/35 bg-[#0B0B0D] px-6 py-4 focus-within:border-[#FFD86A] transition-colors">
          <SearchIcon className="h-5 w-5 text-[#D4AF37]" />
          <input
            autoFocus
            data-testid="search-page-input"
            value={text}
            onChange={(e) => search(e.target.value)}
            placeholder="Titles, genres, cast, directors..."
            className="w-full bg-transparent text-base sm:text-lg text-white outline-none placeholder:text-[#5A5A62]"
          />
        </label>
        <div className="mt-5 flex flex-wrap gap-2">
          {SUGGESTIONS.map((s) => (
            <button key={s} data-testid={`search-suggestion-${s.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => search(s)} className="rounded-full border border-white/15 px-4 py-1.5 text-sm text-white/70 hover:border-[#FFD86A] hover:text-[#FFD86A] transition-colors">{s}</button>
          ))}
        </div>
      </PageHeader>
      <div className="mx-auto max-w-[1680px] px-4 sm:px-6 lg:px-10">
        {results === undefined && <Spinner />}
        {results === null && (
          <>
            <h2 className="mb-6 font-display text-base md:text-lg uppercase tracking-[0.28em] gold-text">Browse everything</h2>
            <TitleGrid items={all} testId="search-all-grid" />
          </>
        )}
        {Array.isArray(results) && (results.length ? (
          <>
            <p data-testid="search-results-count" className="mb-6 text-sm text-[#8B8B8B]">{results.length} result{results.length > 1 ? "s" : ""} for “<span className="text-[#FFD86A]">{q}</span>”</p>
            <TitleGrid items={results} testId="search-results-grid" />
          </>
        ) : <EmptyState icon={SearchX} title="No matches" text={`We couldn't find anything for “${q}”. Try a genre or cast name.`} testId="search-empty" />)}
      </div>
    </div>
  );
}
